import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { adminApi } from '../../services/api';

const ShortUrlManagementPage = ({ caseId: propCaseId, onCaseUpdated }) => {
  const { caseId: paramCaseId } = useParams();
  const caseId = propCaseId || paramCaseId;
  const navigate = useNavigate();
  const [caseDetails, setCaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State to control unlock form visibility
  const [showUnlockForm, setShowUnlockForm] = useState(true);

  // State for editable patient contact info
  const [editablePhone, setEditablePhone] = useState('');
  const [editableEmail, setEditableEmail] = useState('');

  // State for the unlock form
  const [unlockFormData, setUnlockFormData] = useState({
    reason: '',
    requested_by: 'Patient',
    requestor_name: '',
    request_channel: 'Email',
    request_date_time: '',
    admin_name: '',
    admin_email: '',
    comments: ''
  });
  const [unlockFormErrors, setUnlockFormErrors] = useState({});
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [unlockSuccess, setUnlockSuccess] = useState(null);

  // State for resending short URL
  const [resendMessage, setResendMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(null);

  // Fetch case details function
  const fetchCaseDetails = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getCaseDetails(caseId);
      if (response.data && response.data.success) {
        const caseData = response.data.case;
        setCaseDetails(caseData);
        // Initialize form data with current date/time and potentially admin info if available
        const now = new Date();
        const formattedDateTime = now.toISOString().slice(0, 16); // Format as YYYY-MM-DDTHH:MM
        setUnlockFormData(prevData => ({
          ...prevData,
          request_date_time: formattedDateTime,
          // Add admin name/email if available from user context (placeholder)
          admin_name: 'Admin User', // Replace with actual admin name
          admin_email: 'admin@ankrhealth.com' // Replace with actual admin email
        }));
        // Initialize editable phone and email with patient data
        if (caseData.patient_info_fk) {
          setEditablePhone(caseData.patient_info_fk.phone || '');
          setEditableEmail(caseData.patient_info_fk.email || '');
        } else {
          setEditablePhone('');
          setEditableEmail('');
        }
      } else {
        setError(response.data?.message || 'Failed to fetch case details');
      }
    } catch (err) {
      console.error('Error fetching case details:', err);
      setError(err.message || 'An error occurred while fetching case details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) {
      fetchCaseDetails();
    }
  }, [caseId]);

  useEffect(() => {
    if (caseDetails) {
      const now = new Date();
      const expirationTime = new Date(caseDetails.short_url_expiration_time);
      const isExpired = expirationTime < now;
      const isLockedOut = caseDetails.mira_sol_case_status === 'Locked - Attempts exceeded';

      // Automatically hide the unlock form if not expired and not locked out
      if (!isExpired && !isLockedOut) {
        setShowUnlockForm(false);
      } else {
        setShowUnlockForm(true);
      }
    }
  }, [caseDetails]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Handle form input changes
  const handleUnlockFormChange = (e) => {
    const { id, value } = e.target;
    setUnlockFormData({ ...unlockFormData, [id]: value });
  };

  // Validate unlock form
  const validateUnlockForm = () => {
    const errors = {};
    if (!unlockFormData.reason) errors.reason = 'Reason is required.';
    if (!unlockFormData.requested_by) errors.requested_by = 'Requested by is required.';
    if (unlockFormData.requested_by !== 'Patient' && !unlockFormData.requestor_name) {
      errors.requestor_name = 'Requestor Name is required.';
    }
    if (!unlockFormData.request_channel) errors.request_channel = 'Request Channel is required.';
    if (!unlockFormData.request_date_time) errors.request_date_time = 'Request Date & Time is required.';
    if (!unlockFormData.admin_name) errors.admin_name = 'Admin Name is required.';
    if (!unlockFormData.admin_email) {
      errors.admin_email = 'Admin Email is required.';
    } else if (!/\S+@ankrhealth\.com$|\S+@ankr\.us$/.test(unlockFormData.admin_email)) {
      errors.admin_email = 'Admin Email must end with @ankrhealth.com or @ankr.us.';
    }
    setUnlockFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle unlock form submission
  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    if (!validateUnlockForm()) {
      return;
    }

    setIsUnlocking(true);
    setUnlockSuccess(null);
    try {
      const response = await adminApi.unlockShortUrl({
        action: 'unlock', // Specify the action
        case_identifier: caseId, // Use case ID from URL params
        ...unlockFormData
      });

      if (response.data.success) {
        setUnlockSuccess({ type: 'success', message: response.data.message });
        // Hide the form on successful submission
        setShowUnlockForm(false);
        // Re-fetch case details to update the displayed information
        const updatedCaseResponse = await adminApi.getCaseDetails(caseId);
        if (updatedCaseResponse.data?.success) {
          setCaseDetails(updatedCaseResponse.data.case);
          // Notify parent component of the update to refresh the grid
          if (onCaseUpdated) {
            onCaseUpdated(updatedCaseResponse.data.case);
          }
        }
      } else {
        setUnlockSuccess({ type: 'danger', message: response.data.message || 'Failed to unlock short URL.' });
      }
    } catch (err) {
      console.error('Error unlocking short URL:', err);
      setUnlockSuccess({ type: 'danger', message: err.response?.data?.message || 'An error occurred while unlocking short URL.' });
    } finally {
      setIsUnlocking(false);
    }
  };

  // Handle resend short URL
  const handleResendShortUrl = async () => {
    if (!caseDetails || !caseDetails.short_url) return;

    setIsResending(true);
    setResendSuccess(null);
    try {
      const response = await adminApi.unlockShortUrl({ // Use the unified endpoint
        action: 'resend', // Specify the action
        case_identifier: caseId, // Use case ID from URL params
        message: resendMessage
      });

      if (response.data.success) {
        setResendSuccess({ type: 'success', message: response.data.message });
      } else {
        setResendSuccess({ type: 'danger', message: response.data.message || 'Failed to resend short URL.' });
      }
    } catch (err) {
      setResendSuccess({ type: 'danger', message: err.response?.data?.message || 'An error occurred while resending short URL.' });
    } finally {
      setIsResending(false);
    }
  };

  if (loading) {
    return (
      <>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading case details...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="alert alert-danger">{error}</div>
      </>
    );
  }

  if (!caseDetails) {
    return (
      <>
        <div className="alert alert-warning">Case details not found.</div>
      </>
    );
  }

  // Handlers for editable phone and email
  const handlePhoneChange = (e) => {
    setEditablePhone(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEditableEmail(e.target.value);
  };

  return (
    <>
      {!caseId && (<div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Manage Short URL</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/admin/cases')}>
          Back to Cases
        </button>
      </div>)}

      {/* Case Information */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="card-title text-primary">Case Details</div>
          <p><strong>Case ID:</strong> {caseDetails.CoreServer_CaseID || caseDetails.id}</p>
          <p>
            <strong>Patient:</strong>{' '}
            {caseDetails.patient_info_fk ? (
              `${caseDetails.patient_info_fk.first_name} ${caseDetails.patient_info_fk.last_name} (${caseDetails.patient_info_fk.dob})`
            ) : (
              'N/A'
            )}
          </p>
          <p><strong>Short URL:</strong> {caseDetails.short_url || 'N/A'}</p>
          <p><strong>Valid Till (UTC):</strong> {formatDate(caseDetails.short_url_expiration_time)}</p>
          <p className={caseDetails.mira_sol_case_status === 'Locked - Attempts exceeded' ? 'text-danger' : ''}><strong>Current Status:</strong> {caseDetails.mira_sol_case_status}</p>
          <p className={caseDetails.mira_sol_case_status === 'Locked - Attempts exceeded' ? 'text-danger' : ''}><strong>Access Attempts:</strong> {caseDetails.shorturl_access_attempts || 0}</p>
        </div>
      </div>

      {/* Unlock Short URL Section */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="card-title text-primary">Unlock or Extend Short URL Expiration</div>
          <p className='text-secondary small mb-3'>This allows you to unlock patient&apos;s shortURL if they had too many unsuccessful attempts, or extend expiration time by 7 days if the link has expired. Use with caution.</p>
          {unlockSuccess && (
            <div className={`alert mt-3 alert-${unlockSuccess.type}`} role="alert">
              {unlockSuccess.message}
            </div>
          )}
          {!showUnlockForm && ( // Only show button if form is currently hidden
            <button className='btn btn-info' onClick={() => setShowUnlockForm(true)}>View</button>
          )}
          {showUnlockForm && (<form onSubmit={handleUnlockSubmit}>
            <div className="mb-3">
              <label htmlFor="reason" className="form-label">Reason*</label>
              <textarea
                className={`form-control ${unlockFormErrors.reason ? 'is-invalid' : ''}`}
                id="reason"
                rows="3"
                required
                value={unlockFormData.reason}
                onChange={handleUnlockFormChange}
              ></textarea>
              {unlockFormErrors.reason && <div className="invalid-feedback">{unlockFormErrors.reason}</div>}
            </div>
            <div className="mb-3">
              <label htmlFor="requested_by" className="form-label">Requested by*</label>
              <select
                className={`form-select ${unlockFormErrors.requested_by ? 'is-invalid' : ''}`}
                id="requested_by"
                required
                value={unlockFormData.requested_by}
                onChange={handleUnlockFormChange}
              >
                <option value="Patient">Patient</option>
                <option value="Clinic Staff">Clinic Staff</option>
                <option value="Doctor">Doctor</option>
                <option value="Ankr Staff">Ankr Staff</option>
                <option value="Other">Other</option>
              </select>
              {unlockFormErrors.requested_by && <div className="invalid-feedback">{unlockFormErrors.requested_by}</div>}
            </div>
            {/* Conditional field for requestor name */}
            {unlockFormData.requested_by && unlockFormData.requested_by !== 'Patient' && (
              <div className="mb-3">
                <label htmlFor="requestor_name" className="form-label">Requestor name</label>
                <input
                  type="text"
                  className={`form-control ${unlockFormErrors.requestor_name ? 'is-invalid' : ''}`}
                  id="requestor_name"
                  value={unlockFormData.requestor_name}
                  onChange={handleUnlockFormChange}
                  required={unlockFormData.requested_by !== 'Patient'} // Make required if not Patient
                />
                {unlockFormErrors.requestor_name && <div className="invalid-feedback">{unlockFormErrors.requestor_name}</div>}
              </div>
            )}
            <div className="mb-3">
              <label htmlFor="request_channel" className="form-label">Request channel*</label>
              <select
                className={`form-select ${unlockFormErrors.request_channel ? 'is-invalid' : ''}`}
                id="request_channel"
                required
                value={unlockFormData.request_channel}
                onChange={handleUnlockFormChange}
              >
                <option value="Email">Email</option>
                <option value="Phone">Phone</option>
                <option value="Support Ticket">Support Ticket</option>
                <option value="Other">Other</option>
              </select>
              {unlockFormErrors.request_channel && <div className="invalid-feedback">{unlockFormErrors.request_channel}</div>}
            </div>
            <div className="mb-3">
              <label htmlFor="request_date_time" className="form-label">Request date & time*</label>
              <input
                type="datetime-local"
                className={`form-control ${unlockFormErrors.request_date_time ? 'is-invalid' : ''}`}
                id="request_date_time"
                required
                value={unlockFormData.request_date_time}
                onChange={handleUnlockFormChange}
              />
              {unlockFormErrors.request_date_time && <div className="invalid-feedback">{unlockFormErrors.request_date_time}</div>}
            </div>
            <div className="mb-3">
              <label htmlFor="admin_name" className="form-label">Admin name*</label>
              <input
                type="text"
                className={`form-control ${unlockFormErrors.admin_name ? 'is-invalid' : ''}`}
                id="admin_name"
                required
                value={unlockFormData.admin_name}
                onChange={handleUnlockFormChange}
              />
              {unlockFormErrors.admin_name && <div className="invalid-feedback">{unlockFormErrors.admin_name}</div>}
            </div>
            <div className="mb-3">
              <label htmlFor="admin_email" className="form-label">Admin email*</label>
              <input
                type="email"
                className={`form-control ${unlockFormErrors.admin_email ? 'is-invalid' : ''}`}
                id="admin_email"
                required
                value={unlockFormData.admin_email}
                onChange={handleUnlockFormChange}
              />
              {unlockFormErrors.admin_email && <div className="invalid-feedback">{unlockFormErrors.admin_email}</div>}
            </div>
            <div className="mb-3">
              <label htmlFor="comments" className="form-label">Comments</label>
              <textarea
                className={`form-control ${unlockFormErrors.comments ? 'is-invalid' : ''}`}
                id="comments"
                rows="3"
                value={unlockFormData.comments}
                onChange={handleUnlockFormChange}
              ></textarea>
              {unlockFormErrors.comments && <div className="invalid-feedback">{unlockFormErrors.comments}</div>}
            </div>
            {/* Unlock Button */}
            <button type="submit" className="btn btn-info" disabled={isUnlocking}>
              {isUnlocking ? 'Unlocking...' : 'Unlock Short URL'}
            </button>
            <button className='btn btn-secondary mx-2' onClick={() => setShowUnlockForm(false)}>Cancel</button>
          </form>)}
        </div>
      </div>

      {/* Resend Short URL Section */}
      <div className="card">
        <div className="card-body">
          <div className='card-title text-primary'>Resend Short URL</div>
          {resendSuccess && (
            <div className={`alert mt-3 alert-${resendSuccess.type}`} role="alert">
              {resendSuccess.message}
            </div>
          )}
          <div className="mb-3">
            <label htmlFor="patientPhone" className="form-label">Patient phone</label>
            <input
              type="phone"
              className="form-control"
              id="patientPhone"
              value={editablePhone}
              onChange={handlePhoneChange}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="patientEmail" className="form-label">Patient email</label>
            <input
              type="email"
              className="form-control"
              id="patientEmail"
              value={editableEmail}
              onChange={handleEmailChange}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="resendMessage" className="form-label">Optional message (max 150 chars):</label>
            <textarea
              className="form-control" defaultValue={`Here's your link for the Ankr Visit: ${caseDetails.short_url}`}
              id="resendMessage"
              rows="2"
              maxLength="150"
              value={resendMessage}
              onChange={(e) => setResendMessage(e.target.value)}
            ></textarea>
          </div>
          {/* Resend Button */}
          <button onClick={handleResendShortUrl} className="btn btn-primary" disabled={isResending}>
            {isResending ? 'Sending...' : 'Resend Short URL'}
          </button>
        </div>
      </div>
    </>
  );
};
export default ShortUrlManagementPage;